import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";

const TABS = [
  { id: "business", label: "どんなことをしているの？", shortLabel: "事業内容" },
  { id: "work", label: "仕事内容", shortLabel: "仕事内容" },
  { id: "training", label: "研修制度", shortLabel: "研修制度" },
  { id: "fresh", label: "新卒採用", shortLabel: "新卒採用" },
  { id: "career", label: "キャリア採用", shortLabel: "キャリア採用" },
  { id: "requirements", label: "応募要項", shortLabel: "応募要項" },
] as const;

type TabId = (typeof TABS)[number]["id"];

function SectionHeading({ title, subtitle }: { title: string; subtitle?: ReactNode }) {
  return (
    <div className="mb-8 md:mb-10">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-1.5 h-8 rounded-full bg-primary-500" />
        <h2 className="text-2xl md:text-3xl font-bold text-foreground-900">{title}</h2>
      </div>
      {subtitle && (
        <p className="text-sm md:text-base text-foreground-600 ml-[18px]">{subtitle}</p>
      )}
    </div>
  );
}

const recruitRequirements = [
  { label: "仕事内容", value: "ガス腐食試験に使用する装置の点検や動作確認などの検査アシスタント業務", multiline: true },
  { label: "応募資格", value: "高卒以上" },
  { label: "募集人数", value: "若干名" },
  { label: "職　　種", value: "試験の測定等検査補助（正社員／パートも可）" },
  { label: "勤務地", value: "東京試験センター\n〒146-0084 東京都大田区南久が原2-23-19", multiline: true },
  { label: "就業時間", value: "8：30～17：30" },
  { label: "休　　日", value: "週休二日制（土・日・祝日）\n夏季・年末年始等", multiline: true },
  { label: "給　　与", value: "弊社規定に準ずる\n賞与実績あり／交通費支給／社会保険完備", multiline: true },
  { label: "福利厚生", value: "社会保険完備（健康保険・厚生年金・雇用保険・労災保険）\n交通費全額支給／資格取得支援制度（費用全額会社負担）\n定期健康診断", multiline: true },
];

export default function Recruit() {
  const [activeTab, setActiveTab] = useState<TabId>("business");

  return (
    <PageLayout>
      {/* Page Header */}
      <div className="relative py-14 md:py-20 bg-primary-700 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img
            src="https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/d57b3cff-3df3-467b-82bb-1d97291d0fa4_compressed_IMG_5604-1.webp"
            alt="会議室"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <nav className="mb-4">
              <ol className="flex items-center gap-2 text-sm text-background-300">
                <li>
                  <Link to="/" className="hover:text-background-100 transition-colors">
                    ホーム
                  </Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-xs" />
                </li>
                <li className="text-background-100">採用情報</li>
              </ol>
            </nav>
            <h1 className="text-3xl md:text-4xl font-black text-background-50">採用情報</h1>
            <p className="text-accent-300 text-sm mt-2">careers / join us</p>
          </div>
        </div>
      </div>

      {/* Welcome message */}
      <div className="w-full px-4 md:px-8 pb-0 pt-12 md:pt-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-accent-50 rounded-xl p-6 md:p-8 border border-accent-200/70">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-500 shrink-0">
                <i className="ri-chat-smile-2-line text-xl text-background-50" />
              </div>
              <div>
                <p className="text-sm md:text-base text-foreground-800 leading-loose">
                  当社では未経験の方も大歓迎です。実は今働いている従業員のほとんどが、この業界は初めてという方ばかり。
                  入社後に先輩スタッフが一から丁寧に指導しますので、専門知識がなくても安心してスタートできます。
                  「モノづくりに興味がある」「コツコツとした作業が好き」そんな方なら、きっと活躍できる職場です。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="w-full px-4 md:px-8 pt-10 md:pt-14">
        <div className="max-w-4xl mx-auto">
          <div className="flex overflow-x-auto md:flex-wrap gap-2 pb-2 md:pb-0">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer shrink-0 ${
                    isActive
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200/70 hover:text-foreground-900"
                  }`}
                >
                  <span className="hidden md:inline">{tab.label}</span>
                  <span className="md:hidden">{tab.shortLabel}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== Section 2: 事業内容 ===== */}
      {activeTab === "business" && (
        <section className="w-full px-4 md:px-8 py-10 md:py-16">
          <div className="max-w-4xl mx-auto">
            <SectionHeading
              title="ファクトケイってどんなことをしているの？"
            />

            {/* Answer */}
            <p className="text-sm md:text-base text-foreground-700 leading-loose mb-10 md:mb-12">
              主に<strong>ガス腐食試験</strong>と<strong>その他受託試験</strong>を行っています。
            </p>

            {/* ガス腐食試験とは */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-center mb-12 md:mb-16">
              <div className="lg:order-1 space-y-5">
                <div className="relative bg-primary-50 rounded-2xl rounded-bl-md p-4 md:p-5">
                  <div className="flex items-center gap-3">
                    <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">Q</span>
                    <h3 className="text-lg md:text-xl font-bold text-foreground-900">ガス腐食試験ってなに？</h3>
                  </div>
                  <div className="absolute -bottom-2 left-8 w-4 h-4 bg-primary-50 rotate-45" />
                </div>
                <div className="relative bg-secondary-50 rounded-2xl rounded-bl-md p-4 md:p-5">
                  <div className="flex items-start gap-3">
                    <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-secondary-500 text-background-50 text-xs font-bold">A</span>
                    <div className="space-y-4 pt-1">
                      <p className="text-sm md:text-base text-foreground-700 leading-loose">
                        日本にはいろんな環境試験があります。例えば建物の耐震試験、スマホの防水試験、車部品の高温試験——どれも「長く安心して使えるか」をチェックするためのテストです。<strong>ガス腐食試験もそのひとつ。</strong>電子機器のコネクタやスイッチなどの接触部品が、空気中の腐食性ガスで劣化しないかを調べる試験なんです。
                      </p>
                      <p className="text-sm md:text-base text-foreground-700 leading-loose">
                        目に見えないけど、空気中には硫化水素や亜硫酸ガス、二酸化窒素、オゾンといった腐食性ガスがわずかに含まれています。これらが長い時間をかけて接触部分を劣化させると、自動車の安全装置の誤作動やスマホの充電不良など、<strong>重大なトラブルにつながることも。</strong>ガス腐食試験は実際より高濃度のガスで短期間に再現し、製品の信頼性を事前にしっかり確かめる、縁の下の力持ち的な試験です。
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {[
                    "硫化水素 H₂S",
                    "亜硫酸ガス SO₂",
                    "二酸化窒素 NO₂",
                    "塩素ガス Cl₂",
                    "オゾン O₃",
                    "混合ガス試験",
                  ].map((gas, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-100 text-primary-800 text-xs font-medium"
                    >
                      <i className="ri-check-line" />
                      {gas}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-foreground-500">
                  単一ガスから混合ガスまで、JIS・IEC・各社個別規格に対応したガス腐食試験を実施しています。
                </p>
              </div>
              <div className="lg:order-2">
                <div className="rounded-xl overflow-hidden border border-background-200/70 shadow-sm">
                  <img
                    src="https://storage.readdy-site.link/project_files/46c769b0-62ed-4752-96b8-d59840c4cf62/774725c2-be14-45fd-a593-2aace2beeb1f_compressed_IMG_5485.webp"
                    alt="ガス腐食試験機 KG200"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>

            {/* 受託試験とは */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-center">
              <div className="order-2 lg:order-1">
                <div className="rounded-xl overflow-hidden border border-background-200/70 shadow-sm">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20business%20consultation%20scene%20in%20a%20modern%20office%2C%20engineer%20and%20client%20reviewing%20technical%20test%20samples%20and%20documents%20on%20a%20clean%20desk%2C%20warm%20professional%20atmosphere%2C%20soft%20natural%20lighting%2C%20corporate%20meeting%20setting%2C%20clean%20neutral%20background%2C%20editorial%20photography%20style&width=600&height=400&seq=factk-recruit-business-02&orientation=landscape"
                    alt="受託試験のコンサルテーション"
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2 space-y-5">
                <div className="relative bg-accent-50 rounded-2xl rounded-bl-md p-4 md:p-5">
                  <div className="flex items-center gap-3">
                    <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-xs font-bold">Q</span>
                    <h3 className="text-lg md:text-xl font-bold text-foreground-900">受託試験ってなに？</h3>
                  </div>
                  <div className="absolute -bottom-2 left-8 w-4 h-4 bg-accent-50 rotate-45" />
                </div>
                <div className="relative bg-secondary-50 rounded-2xl rounded-bl-md p-4 md:p-5">
                  <div className="flex items-start gap-3">
                    <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-secondary-500 text-background-50 text-xs font-bold">A</span>
                    <div className="space-y-4 pt-1">
                      <p className="text-sm md:text-base text-foreground-700 leading-loose">
                        「うちの製品、ちゃんと耐久性あるのかな？」——モノづくりをしている企業なら、誰でも一度は気にしたことがあるはず。でもガス腐食試験機ってすごく高価だし、使いこなすには専門的な知識も必要ですよね。そこで登場するのが<strong>受託試験</strong>。お客様から試験サンプルをお預かりして、ファクトケイの試験設備と専門スタッフが試験をまるごと代行する、当社の主力サービスです。
                      </p>
                      <p className="text-sm md:text-base text-foreground-700 leading-loose">
                        試験したいサンプルを送っていただければ、あとは全部お任せ。自社に試験設備がなくても、試験のノウハウがなくても大丈夫。「品質はちゃんと保証したいけど、試験のことはよくわからない」——そんな企業の皆様の強い味方として、高品質な試験結果をしっかりお届けします。
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    {
                      step: "STEP 1",
                      icon: "ri-mail-send-line",
                      title: "ご依頼・サンプル送付",
                      desc: "試験内容のご相談からお見積りまで、専任スタッフが対応。",
                    },
                    {
                      step: "STEP 2",
                      icon: "ri-tools-line",
                      title: "試験実施",
                      desc: "ご指定の規格・条件に基づき、最適な試験機で評価を実施。",
                    },
                    {
                      step: "STEP 3",
                      icon: "ri-file-chart-line",
                      title: "結果報告",
                      desc: "試験データをまとめた報告書を作成し、サンプルとともにご返却。",
                    },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="bg-background-100 rounded-xl p-4 border border-background-200/70 relative"
                    >
                      <span className="absolute top-2 right-2 text-[10px] font-bold text-accent-500 tracking-wider">
                        {item.step}
                      </span>
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-2">
                        <i className={`${item.icon} text-base`} />
                      </div>
                      <h4 className="text-xs font-bold text-foreground-900 mb-1">{item.title}</h4>
                      <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== Section 3: 仕事内容 ===== */}
      {activeTab === "work" && (
        <section className="w-full px-4 md:px-8 py-10 md:py-16">
          <div className="max-w-4xl mx-auto">
            <SectionHeading
              title="仕事内容"
              subtitle="技術職・事務職、それぞれの業務内容をご紹介"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* 技術職 */}
              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-tools-line text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground-900">技術職</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">試験・測定・装置管理</p>
                  </div>
                </div>
                <ul className="space-y-3">
                  {[
                    "ガス腐食試験機の操作・運転管理（装置の起動・停止・条件設定）",
                    "試験前後の装置点検・メンテナンス（日常点検・定期保守）",
                    "接触抵抗測定の実施（測定条件設定・データ取得）",
                    "試験データの記録・報告書作成（Excel・専用ソフトでのデータ整理）",
                    "試験サンプルの受入・出荷管理（受付・梱包・発送手続き）",
                    "品質管理業務（校正管理・測定器管理・不適合対応）",
                  ].map((item, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-foreground-700">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0 mt-0.5">
                        <i className="ri-check-line text-xs" />
                      </div>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-4 border-t border-background-200/70">
                  <div className="flex items-center gap-2 text-xs text-foreground-600">
                    <i className="ri-map-pin-line text-primary-500" />
                    <span>主な勤務地：東京試験センター（大田区南久が原）</span>
                  </div>
                </div>
              </div>

              {/* 事務職 */}
              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 shrink-0">
                    <i className="ri-file-text-line text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground-900">事務職</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">受付・営業事務・総務</p>
                  </div>
                </div>
                <ul className="space-y-3">
                  {[
                    "受託試験の受付・スケジュール管理（依頼内容確認・試験計画立案・進捗管理）",
                    "試験報告書の作成・校正・発行（データ入力・図表作成・文書管理）",
                    "顧客対応（試験進捗のご案内・見積もり作成・請求業務）",
                    "営業支援（カタログ・資料の送付、展示会・セミナーの準備・事務対応）",
                    "総務業務（備品管理・発注処理・電話・来客対応・郵便物管理）",
                    "データ管理（試験データの入力作業・ファイル管理・バックアップ対応）",
                  ].map((item, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-foreground-700">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 shrink-0 mt-0.5">
                        <i className="ri-check-line text-xs" />
                      </div>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-4 border-t border-background-200/70">
                  <div className="flex items-center gap-2 text-xs text-foreground-600">
                    <i className="ri-map-pin-line text-accent-500" />
                    <span>主な勤務地：東京試験センター（大田区南久が原）</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-primary-50 rounded-xl p-5 md:p-6 border border-primary-100 flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500 shrink-0">
                <i className="ri-lightbulb-line text-lg text-background-50" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-foreground-900 mb-1">未経験者大歓迎</h4>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  技術職・事務職ともに業界未経験の方も大歓迎です。入社後は先輩スタッフが一から丁寧に指導しますので、
                  専門知識がなくても安心してスタートできます。意欲と学ぶ姿勢があれば、誰でも活躍できる職場です。
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== Section 4: 研修制度 ===== */}
      {activeTab === "training" && (
        <section className="w-full px-4 md:px-8 py-10 md:py-16">
          <div className="max-w-4xl mx-auto">
            <SectionHeading
              title="研修制度"
              subtitle="未経験からプロへ。半年間の段階的な育成プログラム"
            />
            <div className="space-y-6">
              {[
                {
                  period: "入社後 1〜2ヶ月",
                  icon: "ri-book-open-line",
                  title: "基礎研修",
                  points: [
                    "会社の理念・事業内容の理解",
                    "安全教育・設備の基礎知識",
                    "測定機器の基本的な取扱い",
                    "業界用語・基礎技術用語の習得",
                  ],
                },
                {
                  period: "入社後 2〜4ヶ月",
                  icon: "ri-settings-3-line",
                  title: "技術研修",
                  points: [
                    "ガス腐食試験機の操作習得",
                    "接触抵抗測定の実践トレーニング",
                    "試験規格（JIS・IEC）の読み方・解釈",
                    "データ記録・報告書作成の基本",
                  ],
                },
                {
                  period: "入社後 4〜6ヶ月",
                  icon: "ri-user-star-line",
                  title: "実践研修（OJT）",
                  points: [
                    "先輩スタッフとペアでの実案件対応",
                    "お客様サンプルの取扱い実習",
                    "試験計画の立案補助",
                    "品質管理プロセスの理解と実践",
                  ],
                },
              ].map((phase, i) => (
                <div
                  key={i}
                  className="bg-background-100 rounded-xl p-5 md:p-7 border border-background-200/70 flex flex-col md:flex-row md:items-start gap-5"
                >
                  <div className="flex items-center gap-3 shrink-0 md:w-48">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                      <i className={`${phase.icon} text-lg`} />
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-foreground-900">{phase.title}</h4>
                      <p className="text-xs text-foreground-500 mt-0.5">{phase.period}</p>
                    </div>
                  </div>
                  <ul className="space-y-1.5 flex-1">
                    {phase.points.map((point, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-foreground-700">
                        <i className="ri-arrow-right-s-line text-primary-500 mt-0.5 shrink-0" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-6 bg-accent-50 rounded-xl p-5 md:p-6 border border-accent-200/70 flex items-start gap-3">
              <i className="ri-information-line text-accent-500 text-lg mt-0.5 shrink-0" />
              <p className="text-sm text-foreground-700 leading-relaxed">
                研修期間中も正社員と同様の給与・待遇を保証。資格取得支援制度もあり、試験機器関連の資格取得費用は会社が全額負担します。
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ===== Section 5: 新卒採用 ===== */}
      {activeTab === "fresh" && (
        <section className="w-full px-4 md:px-8 py-10 md:py-16">
          <div className="max-w-4xl mx-auto">
            <SectionHeading
              title="新卒採用"
              subtitle="2027年度 新卒採用募集要項"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-graduation-cap-line text-lg" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground-900">募集対象</h3>
                </div>
                <ul className="space-y-2.5">
                  {[
                    { label: "対象", value: "2027年3月 大学・大学院・短大・専門学校・高等学校 卒業見込の方" },
                    { label: "学科", value: "不問（文系・理系どちらも歓迎）" },
                    { label: "募集職種", value: "試験技術職、営業技術職" },
                    { label: "勤務地", value: "東京試験センター（大田区南久が原）" },
                  ].map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm">
                      <span className="text-primary-600 font-medium shrink-0">{item.label}</span>
                      <span className="text-foreground-700">{item.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                    <i className="ri-heart-line text-lg" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground-900">求める人物像</h3>
                </div>
                <ul className="space-y-2.5">
                  {[
                    "モノづくりや技術に興味がある方",
                    "コツコツと丁寧に作業できる方",
                    "新しいことを学ぶ意欲がある方",
                    "チームで協力して仕事を進められる方",
                  ].map((point, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-check-line text-accent-500 mt-0.5 shrink-0" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="mt-6 bg-primary-50 rounded-xl p-5 md:p-6 border border-primary-100">
              <p className="text-sm text-foreground-700 leading-relaxed">
                <strong>選考プロセス：</strong>
                書類選考 → 会社説明会・適性検査 → 面接（1〜2回） → 内定
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ===== Section 6: キャリア採用 ===== */}
      {activeTab === "career" && (
        <section className="w-full px-4 md:px-8 py-10 md:py-16">
          <div className="max-w-4xl mx-auto">
            <SectionHeading
              title="キャリア採用"
              subtitle="即戦力としてご活躍いただける方を募集しています"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-briefcase-line text-lg" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground-900">募集職種</h3>
                </div>
                <ul className="space-y-3">
                  {[
                    { title: "試験技術スタッフ", exp: "実務経験3年以上歓迎", desc: "ガス腐食試験・接触抵抗測定の実務を担当いただきます" },
                    { title: "営業技術スタッフ", exp: "業界経験者優遇", desc: "技術的な知識を活かした提案型営業をお任せします" },
                    { title: "品質管理スタッフ", exp: "ISO9001経験者歓迎", desc: "試験品質の管理・改善、規格対応をリードいただきます" },
                  ].map((job, j) => (
                    <li key={j} className="pb-3 border-b border-background-200/70 last:border-0 last:pb-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-bold text-foreground-900">{job.title}</h4>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap">
                          {job.exp}
                        </span>
                      </div>
                      <p className="text-xs text-foreground-600">{job.desc}</p>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                    <i className="ri-star-line text-lg" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground-900">歓迎する経験・スキル</h3>
                </div>
                <ul className="space-y-2.5">
                  {[
                    "試験・測定機器の取扱い経験",
                    "電気・電子部品業界での業務経験",
                    "ISO9001／IATF16949の知識",
                    "JIS・IEC規格に関する知識",
                    "品質保証・品質管理の実務経験",
                  ].map((skill, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-foreground-700">
                      <i className="ri-check-line text-accent-500 mt-0.5 shrink-0" />
                      <span>{skill}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== Section 7: 応募要項 ===== */}
      {activeTab === "requirements" && (
        <section className="w-full px-4 md:px-8 py-10 md:py-16">
          <div className="max-w-4xl mx-auto">
            <SectionHeading
              title="応募要項"
              subtitle="共通の採用条件・待遇"
            />
            <div className="overflow-hidden rounded-xl border border-background-200/70">
              <table className="w-full text-sm">
                <tbody>
                  {recruitRequirements.map((row, i) => (
                    <tr
                      key={i}
                      className={`${i !== recruitRequirements.length - 1 ? "border-b border-background-200/70" : ""}`}
                    >
                      <th className="w-32 md:w-48 px-4 md:px-6 py-3.5 md:py-4 bg-accent-100 text-accent-900 font-medium text-center align-middle whitespace-nowrap">
                        {row.label}
                      </th>
                      <td className="px-4 md:px-6 py-3.5 md:py-4 text-foreground-800 bg-background-50 align-middle">
                        {row.multiline ? (
                          <div className="space-y-1">
                            {row.value.split("\n").map((line, j) => (
                              <p key={j}>{line}</p>
                            ))}
                          </div>
                        ) : (
                          row.value
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-background-100 rounded-xl p-5 border border-background-200/70">
              <div className="flex items-center gap-2 text-sm text-foreground-700">
                <i className="ri-file-download-line text-primary-500 text-lg" />
                <span>応募書類：履歴書（写真貼付）・職務経歴書（あれば）</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-foreground-700">
                <i className="ri-mail-line text-primary-500 text-lg" />
                <span>応募方法：郵送またはメールにてご送付ください</span>
              </div>
            </div>
            {/* Download + Contact */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-primary-50 rounded-xl p-5 md:p-6 border border-primary-100 flex flex-col items-center text-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500 shrink-0">
                  <i className="ri-file-pdf-line text-xl text-background-50" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-foreground-900 mb-1">募集要項PDF</h4>
                  <p className="text-xs text-foreground-600">採用情報をまとめたPDFをダウンロードできます</p>
                </div>
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); window.print(); }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-download-2-line" />
                  <span>ダウンロード</span>
                </a>
              </div>
              <div className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/70">
                <h4 className="text-sm font-bold text-foreground-900 mb-3 flex items-center gap-2">
                  <i className="ri-phone-line text-primary-500" />
                  応募先・お問い合わせ
                </h4>
                <div className="space-y-2.5">
                  <div className="flex items-start gap-2 text-sm">
                    <i className="ri-mail-line text-foreground-400 mt-0.5 shrink-0" />
                    <div>
                      <span className="text-foreground-600">メール：</span>
                      <a href="mailto:kyujin_uketuke@factk.jp" className="text-primary-600 hover:text-primary-700 transition-colors">
                        kyujin_uketuke@factk.jp
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <i className="ri-smartphone-line text-foreground-400 mt-0.5 shrink-0" />
                    <div>
                      <span className="text-foreground-600">横浜研究室：</span>
                      <a href="tel:045-301-4282" className="text-primary-600 hover:text-primary-700 transition-colors font-medium">
                        045-301-4282
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <i className="ri-user-line text-foreground-400 mt-0.5 shrink-0" />
                    <div>
                      <span className="text-foreground-600">担当者：</span>
                      <span className="text-foreground-900 font-medium">村原</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Contact CTA */}
      <div className="w-full px-4 md:px-8 py-14 md:py-20">
        <div className="max-w-4xl mx-auto">
          <div className="bg-primary-50 rounded-xl p-6 md:p-8 border border-primary-100">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-lg md:text-xl font-bold text-primary-900 mb-2">
                  ご興味のある方はお気軽にお問い合わせください
                </h3>
                <p className="text-sm text-primary-700">
                  応募や職場の雰囲気についてのご質問など、採用担当が対応いたします。
                </p>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap shrink-0"
              >
                <span>お問い合わせはこちら</span>
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}